/**
 * Definition Transformation Utilities
 *
 * Converts between CollaborationDefinition format and ReactFlow format.
 * Preserves all node properties through round-trip conversion.
 */

import { Node, Edge } from '@xyflow/react';
import dagre from 'dagre';
import {
  CollaborationDefinition,
  CollaborationNode,
  CollaborationEdge,
  NodeType,
} from '../types/definition';

// =============================================================================
// Types
// =============================================================================

export interface DefinitionNodeData {
  label: string;
  nodeType: NodeType;
  // Store the original node data for round-trip preservation
  originalNode: CollaborationNode;
  // Index signature required by @xyflow/react v12's Node data constraint
  [key: string]: unknown;
}

export type DefinitionNode = Node<DefinitionNodeData>;
export type DefinitionEdge = Edge;

// =============================================================================
// Default Layout Configuration
// =============================================================================
//
// The index-grid constants that used to live here went with
// `calculateDefaultPosition` (task 771): a grid that ignores edges is what made
// a seeded config open as a tangle. `NODE_SIZE`, beside the dagre layout below,
// is the only geometry left.

// =============================================================================
// Collaboration Definition -> ReactFlow
// =============================================================================

/**
 * Convert a CollaborationDefinition to ReactFlow nodes and edges
 */
export const collaborationToReactFlow = (
  definition: CollaborationDefinition | null | undefined
): { nodes: DefinitionNode[]; edges: DefinitionEdge[] } => {
  if (!definition || !definition.nodes) {
    return { nodes: [], edges: [] };
  }

  /**
   * If ANY node lacks a position the whole graph is laid out (task 771).
   *
   * All-or-nothing on purpose: mixing stored positions with generated ones
   * produces a graph that is neither the arrangement someone saved nor a clean
   * layout. A definition where every node already has a position is passed
   * through untouched.
   */
  const needsLayout = definition.nodes.some((node) => !node.position);

  const nodes = definition.nodes.map((node) => {
    const position = node.position ?? { x: 0, y: 0 };

    return {
      id: node.id,
      type: `${node.type}Node`, // Maps to custom node types: entryNode, agentNode, etc.
      position,
      data: {
        label: getNodeLabel(node),
        nodeType: node.type,
        originalNode: node,
      },
    } as DefinitionNode;
  });

  const edges = (definition.edges || []).map((edge, index) => ({
    id: edge.id || `edge-${index}`,
    source: edge.from,
    target: edge.to,
    label: edge.label || edge.condition || undefined,
    type: 'smoothstep',
    animated: false,
    data: {
      condition: edge.condition,
      originalEdge: edge,
    },
  }));

  // Laid out only when something is missing a position; never written back to
  // the definition, so opening a config leaves the form clean until a real drag.
  return needsLayout ? getLayoutedElements(nodes, edges) : { nodes, edges };
};

/**
 * Lay a graph out with dagre — THE layout, used by both the toolbar's
 * "Auto-layout" button and the default applied to a definition that carries no
 * positions (task 771).
 *
 * It lived only in `DefinitionCanvas`, so a definition with no positions fell
 * back to an index grid that ignored edges entirely — the tangle a seeded
 * config opened as. One implementation now serves both, which is what keeps the
 * button and the default from drifting into two different-looking graphs.
 */
export const getLayoutedElements = <
  N extends { id: string; position: { x: number; y: number } },
  E extends { source: string; target: string },
>(
  nodes: N[],
  edges: E[],
  direction: 'LR' | 'TB' = 'LR'
): { nodes: N[]; edges: E[] } => {
  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: direction, nodesep: 50, ranksep: 100 });

  nodes.forEach((node) => {
    g.setNode(node.id, { width: NODE_SIZE.WIDTH, height: NODE_SIZE.HEIGHT });
  });

  edges.forEach((edge) => {
    // An edge to a node that is not in the graph would make dagre invent one.
    if (nodes.some((n) => n.id === edge.source) && nodes.some((n) => n.id === edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(g);

  return {
    nodes: nodes.map((node) => {
      const laidOut = g.node(node.id);
      return laidOut
        ? {
            ...node,
            // dagre reports centres; ReactFlow positions by top-left corner.
            position: { x: laidOut.x - NODE_SIZE.WIDTH / 2, y: laidOut.y - NODE_SIZE.HEIGHT / 2 },
          }
        : node;
    }),
    edges,
  };
};

/** The node box dagre lays out, and the offset back to a top-left origin. */
export const NODE_SIZE = { WIDTH: 180, HEIGHT: 80 } as const;

/**
 * Get a display label for a node
 */
const getNodeLabel = (node: CollaborationNode): string => {
  if (node.name) return node.name;

  switch (node.type) {
    case 'entry':
      return 'Entry';
    case 'agent':
      return node.alias || 'Agent';
    case 'function':
      return node.function || 'Function';
    case 'branch':
      return node.branchType || 'Branch';
    default:
      return (node as CollaborationNode).id;
  }
};

// =============================================================================
// ReactFlow -> Collaboration Definition
// =============================================================================

/**
 * Convert ReactFlow nodes and edges back to CollaborationDefinition
 */
export const reactFlowToCollaboration = (
  nodes: DefinitionNode[],
  edges: DefinitionEdge[],
  existingDefinition?: CollaborationDefinition | null
): CollaborationDefinition => {
  // Convert ReactFlow nodes back to CollaborationNodes
  const collaborationNodes: CollaborationNode[] = nodes.map((node) => {
    // Start with the original node data if available
    const originalNode = node.data.originalNode;

    // Update position from ReactFlow
    return {
      ...originalNode,
      position: {
        x: node.position.x,
        y: node.position.y,
      },
    } as CollaborationNode;
  });

  // Convert ReactFlow edges back to CollaborationEdges
  const collaborationEdges: CollaborationEdge[] = edges.map((edge) => {
    const originalEdge = edge.data?.originalEdge as CollaborationEdge | undefined;

    return {
      id: edge.id,
      from: edge.source,
      to: edge.target,
      condition: originalEdge?.condition || (typeof edge.label === 'string' ? edge.label : undefined),
      label: originalEdge?.label,
    };
  });

  /**
   * PRESERVE BY DEFAULT (task 877).
   *
   * This used to REBUILD the definition from a hardcoded allow-list of
   * top-level keys — `version`, `nodes`, `edges`, `settings`, `context`,
   * `errorHandling` — which silently deleted every other top-level field of the
   * service's `CollaborationDefinition` on each canvas save. That is how the
   * Assistant config lost `credentialPassthrough`: with the declaration gone no
   * per-turn credential was forwarded, and the `resource-reviewer` node — whose
   * MCP connection is deliberately credential-less, the task-755 fail-closed
   * property — was refused with 401 on every review turn.
   *
   * The canvas owns `nodes` and `edges`; everything else belongs to whoever
   * wrote it. Spreading the stored definition first means the NEXT field the
   * service adds survives without an app change, which is the whole point.
   * `version` is defaulted only when the stored definition supplies none.
   */
  return {
    ...existingDefinition,
    version: existingDefinition?.version ?? '1.0',
    nodes: collaborationNodes,
    edges: collaborationEdges,
  };
};

// =============================================================================
// Node Creation Helpers
// =============================================================================

/**
 * Create a new node with default values for a given type
 */
export const createNewNode = (
  type: NodeType,
  position: { x: number; y: number },
  id?: string
): CollaborationNode => {
  const nodeId = id || `${type}-${Date.now()}`;

  const baseNode = {
    id: nodeId,
    position,
  };

  switch (type) {
    case 'entry':
      return {
        ...baseNode,
        type: 'entry',
        name: 'Entry',
        entry: true,
        input: {},
        output: {},
      };
    case 'agent':
      return {
        ...baseNode,
        type: 'agent',
        name: 'Agent',
        agentId: '', // Must be filled in by user
        input: {},
        output: {},
      };
    case 'function':
      return {
        ...baseNode,
        type: 'function',
        name: 'Function',
        function: 'echo', // Default function
        input: {},
        output: {},
      };
    case 'branch':
      return {
        ...baseNode,
        type: 'branch',
        name: 'Branch',
        branchType: 'conditional',
        condition: '',
        branches: [],
      };
    default:
      throw new Error(`Unknown node type: ${type}`);
  }
};

/**
 * Convert a CollaborationNode to a ReactFlow DefinitionNode
 */
export const collaborationNodeToReactFlow = (node: CollaborationNode): DefinitionNode => {
  return {
    id: node.id,
    type: `${node.type}Node`,
    position: node.position || { x: 0, y: 0 },
    data: {
      label: getNodeLabel(node),
      nodeType: node.type,
      originalNode: node,
    },
  };
};

// =============================================================================
// Validation Helpers
// =============================================================================

/**
 * Check if a definition appears to be in the new graph format
 */
export const isGraphDefinition = (definition: unknown): definition is CollaborationDefinition => {
  if (!definition || typeof definition !== 'object') return false;
  const def = definition as Record<string, unknown>;
  return Array.isArray(def.nodes) && Array.isArray(def.edges);
};

/**
 * Create an empty definition with sensible defaults
 */
export const createEmptyDefinition = (): CollaborationDefinition => ({
  version: '1.0',
  nodes: [],
  edges: [],
  settings: {
    timeout: 60000,
    logging: { level: 'info' },
  },
});
